import { unzipSync, strFromU8 } from 'fflate';
import { meets, ModuleManifestSchema, RESERVED_SHORTCUTS, satisfies, TreeSpecSchema, typeOwner, undeclaredUses, type ModuleManifest } from '@vp/shared-types';

/** Platform API version (independent from the data format version, §8.6). */
export const API_VERSION = '4.10.5';

export const PACKAGE_LIMITS = {
  maxArchiveBytes: 20 * 1024 * 1024,
  maxFiles: 500,
  maxFileBytes: 10 * 1024 * 1024,
  maxTotalBytes: 50 * 1024 * 1024,
};

export class PackageError extends Error {
  readonly code = 'E_PACKAGE';
  constructor(message: string) {
    super(message);
  }
}

export interface ModulePackage {
  manifest: ModuleManifest;
  files: Map<string, Uint8Array>;
}

/** Validate a manifest object (§7.2). Returns a readable reason on failure. */
export function validateManifest(raw: unknown): { ok: true; manifest: ModuleManifest } | { ok: false; reason: string } {
  const r = ModuleManifestSchema.safeParse(raw);
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, reason: `${i?.path.join('.') || 'manifest'}: ${i?.message}` };
  }
  const m = r.data;
  const types = new Set<string>();
  for (const t of m.contributes.objectTypes ?? []) {
    if (types.has(t.type)) return { ok: false, reason: `duplicate object type ${t.type}` };
    types.add(t.type);
  }
  for (const tool of m.contributes.tools ?? []) {
    if (tool.gesture) {
      if (tool.objectType) return { ok: false, reason: `tool ${tool.id}: a gesture tool creates objects itself, objectType is not used` };
      continue;
    }
    if (!tool.objectType) return { ok: false, reason: `tool ${tool.id} needs an objectType or a gesture` };
    if (!types.has(tool.objectType)) return { ok: false, reason: `tool ${tool.id} refers to unknown type ${tool.objectType}` };
  }
  return { ok: true, manifest: m };
}

/** Does the module's API range accept this platform? Incompatible major → not activated (§8.6). */
export function apiCompatible(m: ModuleManifest, api = API_VERSION): boolean {
  return satisfies(api, m.apiVersion);
}

/** Unpack and validate a module archive (structure, limits, manifest, entry). */
export function parsePackage(data: Uint8Array): ModulePackage {
  if (data.byteLength > PACKAGE_LIMITS.maxArchiveBytes) throw new PackageError('archive too large');
  let entries: Record<string, Uint8Array>;
  // sizes are read from the archive's directory BEFORE anything is unpacked: a small file that would
  // inflate to gigabytes (a zip bomb) is refused without being unpacked
  let refused: string | null = null;
  let declared = 0;
  let count = 0;
  try {
    entries = unzipSync(data, {
      filter: (f) => {
        if (f.name.endsWith('/')) return false;
        declared += f.originalSize;
        if (++count > PACKAGE_LIMITS.maxFiles) refused = 'too many files';
        else if (f.originalSize > PACKAGE_LIMITS.maxFileBytes) refused = `file too large: ${f.name}`;
        else if (declared > PACKAGE_LIMITS.maxTotalBytes) refused = 'package too large';
        // false: this entry is not unpacked; the check below throws for the whole archive
        return refused === null;
      },
    });
  } catch {
    throw new PackageError('not a zip archive');
  }
  if (refused) throw new PackageError(refused);
  const names = Object.keys(entries).filter((n) => !n.endsWith('/'));
  if (names.length > PACKAGE_LIMITS.maxFiles) throw new PackageError('too many files');
  let total = 0;
  const files = new Map<string, Uint8Array>();
  for (const n of names) {
    if (n.includes('..') || n.startsWith('/') || n.includes('\\')) throw new PackageError(`invalid path ${n}`);
    const d = entries[n];
    if (d.byteLength > PACKAGE_LIMITS.maxFileBytes) throw new PackageError(`file too large: ${n}`);
    total += d.byteLength;
    files.set(n, d);
  }
  if (total > PACKAGE_LIMITS.maxTotalBytes) throw new PackageError('package too large');
  const mf = files.get('manifest.json');
  if (!mf) throw new PackageError('manifest.json is missing');
  let raw: unknown;
  try {
    raw = JSON.parse(strFromU8(mf));
  } catch {
    throw new PackageError('manifest.json is not valid JSON');
  }
  const v = validateManifest(raw);
  if (!v.ok) throw new PackageError(v.reason);
  if (!files.has(v.manifest.entry)) throw new PackageError(`entry ${v.manifest.entry} is missing`);
  const panel = v.manifest.contributes.panel;
  if (panel && !files.has(panel.file)) throw new PackageError(`panel ${panel.file} is missing`);
  for (const w of v.manifest.contributes.windows ?? []) if (!files.has(w.file)) throw new PackageError(`window ${w.file} is missing`);
  for (const c of v.manifest.contributes.profileCards ?? []) if (!files.has(c.view)) throw new PackageError(`profile card ${c.view} is missing`);
  return { manifest: v.manifest, files };
}

/** A font face with its data embedded (data: URL): what modules, views, the canvas and SVG export use. */
export interface FontFaceData {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  unicodeRange?: string;
  url: string;
}

const FONT_MIME: Record<string, string> = { woff2: 'font/woff2', woff: 'font/woff', ttf: 'font/ttf', otf: 'font/otf' };

function base64(data: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < data.length; i += 0x8000) bin += String.fromCharCode(...data.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** The module's own fonts (manifest `contributes.fonts`) read from its package. Missing files are skipped. */
export function packageFonts(pkg: ModulePackage): FontFaceData[] {
  const out: FontFaceData[] = [];
  for (const font of pkg.manifest.contributes.fonts ?? []) {
    for (const f of font.files) {
      const data = pkg.files.get(f.path);
      if (!data) continue;
      const ext = (/\.(\w+)$/.exec(f.path)?.[1] ?? 'woff2').toLowerCase();
      out.push({ family: font.family, weight: f.weight ?? 400, style: f.style ?? 'normal', ...(f.unicodeRange ? { unicodeRange: f.unicodeRange } : {}), url: `data:${FONT_MIME[ext] ?? 'font/woff2'};base64,${base64(data)}` });
    }
  }
  return out;
}

/** Module locale catalogs: locales/<lang>.json. */
export function packageLocales(pkg: ModulePackage): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [name, data] of pkg.files) {
    const m = /^locales\/([a-z]{2})\.json$/.exec(name);
    if (!m) continue;
    try {
      const json = JSON.parse(strFromU8(data)) as Record<string, unknown>;
      out[m[1]] = Object.fromEntries(Object.entries(json).filter(([, v]) => typeof v === 'string')) as Record<string, string>;
    } catch {
      /* ignore broken locale file */
    }
  }
  return out;
}

export function packageText(pkg: ModulePackage, path: string): string | null {
  const d = pkg.files.get(path);
  return d ? strFromU8(d) : null;
}

/** A manifest field that would do nothing, or a contribution that cannot work (API 2.0: `E_MANIFEST`). */
export class ManifestRuleError extends Error {
  readonly code = 'E_MANIFEST';
}

/**
 * Rules of a package beyond the schema: no field without an effect, references between
 * contributions, template files, format permissions, shortcuts. The same check runs when the app
 * installs a module and when the catalog server accepts one; rules about other installed modules
 * (taken shortcuts, dependencies) are the app's.
 */
export function checkPackageRules(pkg: ModulePackage): void {
  const m = pkg.manifest;
  // API 2.4: each publisher link once
  const urls = (m.links ?? []).map((l) => l.url.toLowerCase());
  if (new Set(urls).size !== urls.length) throw new ManifestRuleError('publisher links must not repeat');
  // API 2.3: window ids are how the module opens them
  const winIds = (m.contributes.windows ?? []).map((w) => w.id);
  if (new Set(winIds).size !== winIds.length) throw new ManifestRuleError('window ids must be unique');
  // API 3.0: a profile card is found by its id
  const cardIds = (m.contributes.profileCards ?? []).map((c) => c.id);
  if (new Set(cardIds).size !== cardIds.length) throw new ManifestRuleError('profile card ids must be unique');
  // API 4.10: a card declares every platform feature its HTML and look rely on
  for (const c of m.contributes.profileCards ?? []) {
    const missing = undeclaredUses(c.uses ?? [], packageText(pkg, c.view) ?? '', c.look);
    if (missing.length) throw new ManifestRuleError(`profile card "${c.id}" uses ${missing.join(', ')} without declaring it in uses`);
    if (c.uses?.includes('live:profile') && !m.permissions.includes('profile.stats')) throw new ManifestRuleError(`profile card "${c.id}": live:profile needs the profile.stats permission`);
  }
  // top bar buttons run the module's own commands
  const commandIds = new Set((m.contributes.commands ?? []).map((c) => c.id));
  const orphan = (m.contributes.toolbar ?? []).find((b) => !commandIds.has(b.command));
  // API 2.0: no field that is accepted but does nothing
  for (const t of m.contributes.objectTypes ?? []) {
    if (t.defaultStyle && !t.capabilities?.style) throw new ManifestRuleError(`type "${t.type}": defaultStyle needs capabilities.style`);
    if (t.view?.fit && t.view.fit !== 'none' && t.renderMode !== 'C') throw new ManifestRuleError(`type "${t.type}": view.fit is for render mode C`);
    const quick = (t.fields ?? []).filter((f) => f.quick);
    if (quick.some((f) => f.type === 'panel')) throw new ManifestRuleError(`type "${t.type}": a panel field cannot be quick`);
    if (quick.length > 4) throw new ManifestRuleError(`type "${t.type}": at most 4 quick fields`);
  }
  if ((m.contributes.settings ?? []).some((f) => f.quick)) throw new ManifestRuleError('settings fields cannot be quick');
  // API 2.2: menu items and shortcuts run the module's own commands
  for (const c of [...(m.contributes.contextMenu ?? []), ...(m.contributes.shortcuts ?? [])]) {
    if (!commandIds.has(c.command)) throw new ManifestRuleError(`menu item or shortcut refers to unknown command "${c.command}"`);
  }
  // API 2.5: feed tools run the module's own commands that need no selection, behind the feed permission
  const feedTools = m.contributes.feedTools ?? [];
  if (feedTools.length && !m.permissions.includes('feed')) throw new ManifestRuleError('feed tools need the feed permission');
  if (!feedTools.length && m.permissions.includes('feed')) throw new ManifestRuleError('the feed permission is for feed tools');
  if (new Set(feedTools.map((f) => f.command)).size !== feedTools.length) throw new ManifestRuleError('a command is in the feed tools twice');
  for (const f of feedTools) {
    const c = m.contributes.commands?.find((x) => x.id === f.command);
    if (!c) throw new ManifestRuleError(`feed tool refers to unknown command "${f.command}"`);
    if (c.objectTypes?.length) throw new ManifestRuleError(`feed tool "${f.command}": the command must not be tied to object types`);
  }
  // API 4.0: one scope per canvas access — `all` already lets the user grant `own`
  for (const kind of ['read', 'write'] as const) {
    if (m.permissions.includes(`canvas.${kind}.all`) && m.permissions.includes(`canvas.${kind}.own`)) throw new ManifestRuleError(`ask for canvas.${kind}.all or canvas.${kind}.own, not both`);
  }
  // API 4.6: keys for object types need the keyboard permission
  const keyed = (m.contributes.objectTypes ?? []).find((t) => t.capabilities?.keyboard);
  if (keyed && !m.permissions.includes('keyboard')) throw new ManifestRuleError(`object type "${keyed.type}": capabilities.keyboard needs the keyboard permission`);
  if ((m.contributes.tools ?? []).some((t) => t.gesture) && !meets(m.permissions, 'canvas.write')) throw new ManifestRuleError('a gesture tool needs the canvas.write permission');
  for (const t of m.contributes.templates ?? []) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(packageText(pkg, t.file) ?? 'null');
    } catch {
      throw new ManifestRuleError(`template "${t.id}": ${t.file} is not valid JSON`);
    }
    const ok = TreeSpecSchema.safeParse(parsed);
    if (!ok.success) throw new ManifestRuleError(`template "${t.id}": ${ok.error.issues[0]?.path.join('.')} ${ok.error.issues[0]?.message}`);
    const foreign = ok.data.objects.find((o) => typeOwner(o.type) && typeOwner(o.type) !== m.id);
    if (foreign) throw new ManifestRuleError(`template "${t.id}": type ${foreign.type} belongs to another module`);
  }
  for (const f of m.contributes.formats ?? []) {
    const need = f.kind === 'import' ? 'canvas.write' : 'canvas.read';
    if (!meets(m.permissions, need)) throw new ManifestRuleError(`${f.kind} format "${f.id}" needs the ${need} permission`);
    if (f.accept && f.kind === 'export') throw new ManifestRuleError(`export format "${f.id}": accept is for imports`);
  }
  const ownKeys = new Set<string>();
  for (const s of m.contributes.shortcuts ?? []) {
    if (RESERVED_SHORTCUTS.has(s.keys)) throw new ManifestRuleError(`shortcut ${s.keys} is kept by the platform`);
    if (ownKeys.has(s.keys)) throw new ManifestRuleError(`shortcut ${s.keys} is used twice`);
    ownKeys.add(s.keys);
  }
  if (orphan) throw new ManifestRuleError(`toolbar button refers to unknown command "${orphan.command}"`);
}
